import type { SalesPoint } from "../../lib/types";
import { parseCsv } from "../products/csv";

export type SalesPointInput = Pick<
  SalesPoint,
  "name" | "kind" | "province" | "canton" | "address" | "hours" | "phone" | "mapsUrl" | "wazeUrl" | "services"
>;

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "");

/** Accepted column names (accents, case and spaces ignored). */
const COLUMNS = {
  name: ["nombre", "punto", "tienda", "name"],
  kind: ["tipo", "kind"],
  province: ["provincia", "province"],
  canton: ["canton", "ciudad", "municipio"],
  address: ["direccion", "address", "senas"],
  hours: ["horario", "horarios", "hours"],
  phone: ["telefono", "tel", "phone", "whatsapp"],
  mapsUrl: ["googlemaps", "google", "maps", "mapa", "ubicacion"],
  wazeUrl: ["waze"],
  services: ["servicios", "services", "notas"],
} as const;

/** "Distribuidor", "centro de servicio"… to the stored kind. */
function kindOf(value: string): SalesPoint["kind"] {
  const v = norm(value);
  if (v.includes("distrib") || v.includes("revend") || v.includes("autoriz")) return "reseller";
  if (v.includes("servicio") || v.includes("taller") || v.includes("tecnic")) return "service";
  return "store";
}

export function readPointsCsv(text: string) {
  const [header = [], ...rows] = parseCsv(text);
  const index: Partial<Record<keyof typeof COLUMNS, number>> = {};
  header.forEach((h, i) => {
    const key = (Object.keys(COLUMNS) as (keyof typeof COLUMNS)[]).find((k) =>
      (COLUMNS[k] as readonly string[]).includes(norm(h)),
    );
    if (key && index[key] === undefined) index[key] = i;
  });
  if (index.name === undefined) return { items: [], skipped: rows.length, missingName: true };
  const cell = (r: string[], k: keyof typeof COLUMNS, max: number) =>
    (index[k] === undefined ? "" : (r[index[k]] ?? "").trim()).slice(0, max) || null;
  let skipped = 0;
  const items: SalesPointInput[] = [];
  for (const r of rows) {
    const name = cell(r, "name", 200);
    if (!name) {
      skipped++;
      continue;
    }
    const link = (k: "mapsUrl" | "wazeUrl") => {
      const v = cell(r, k, 500);
      return v && /^https?:\/\//.test(v) ? v : null;
    };
    items.push({
      name,
      kind: kindOf(cell(r, "kind", 80) ?? ""),
      province: cell(r, "province", 80),
      canton: cell(r, "canton", 80),
      address: cell(r, "address", 500),
      hours: cell(r, "hours", 300),
      phone: cell(r, "phone", 40),
      mapsUrl: link("mapsUrl"),
      wazeUrl: link("wazeUrl"),
      services: cell(r, "services", 1000),
    });
  }
  return { items, skipped, missingName: false };
}

export const POINTS_CSV_TEMPLATE = [
  "nombre,tipo,provincia,canton,direccion,horario,telefono,google maps,waze,servicios",
  '"Tienda Central",Tienda propia,San José,San José,"Avenida 2, calles 5 y 7","Lunes a sábado, 8 a. m. a 6 p. m.",2222-0000,https://maps.app.goo.gl/…,https://waze.com/ul/…,"Recibe garantías, vende repuestos"',
].join("\r\n");
